import {toast} from '@wandb/components';

import type {
  GeneratedThreadTitleResponse,
  WbAgentClient,
} from '../../aria/client/WbAgentClient';
import {publishWBAgentCrossTabEvent} from '../crossTabSync';
import {
  beginRename,
  endRename,
  renameThread as renameThreadAction,
} from '../store/actions';
import type {AgentChatDispatch, AgentChatStore} from '../store/context';
import {defaultAbortControllers} from './renameThread';

export type GenerateThreadTitleParams = {
  client: WbAgentClient;
  dispatch: AgentChatDispatch;
  store: AgentChatStore;
  threadId: string;
  abortControllers?: Map<string, AbortController>;
  /**
   * notifyOnError controls whether a failed request surfaces a toast. Defaults
   * to true for user-initiated renames; automatic background generation passes
   * false since the prompt-snippet fallback still renders a usable label.
   */
  notifyOnError?: boolean;
};

/**
 * generateThreadTitle asks wb_agent to generate and persist a title, then
 * applies the generated title to the local thread. A later title mutation for
 * the same thread aborts and supersedes this one.
 */
export async function generateThreadTitle({
  client,
  dispatch,
  store,
  threadId,
  abortControllers = defaultAbortControllers,
  notifyOnError = true,
}: GenerateThreadTitleParams): Promise<void> {
  const priorThread = store.getState().threads[threadId];
  if (!priorThread) {
    return;
  }

  abortControllers.get(threadId)?.abort();
  const controller = new AbortController();
  abortControllers.set(threadId, controller);

  // The generated title is not known until the request completes. Preserve the
  // current title as the pending overlay so a concurrent history refresh cannot
  // visibly replace it with stale metadata while generation is in flight.
  dispatch(beginRename(threadId, priorThread.title ?? ''));

  try {
    const response = await client.post<GeneratedThreadTitleResponse>(
      `/threads/${threadId}/generate-title`,
      undefined,
      undefined,
      controller.signal
    );

    dispatch(renameThreadAction(threadId, response.title));
    publishWBAgentCrossTabEvent({type: 'thread-renamed', threadId});
    dispatch(endRename(threadId));
  } catch (err) {
    if (controller.signal.aborted || (err as Error).name === 'AbortError') {
      return;
    }

    dispatch(endRename(threadId));
    if (notifyOnError && store.getState().threads[threadId]) {
      toast.error('Failed to generate chat title.');
    }
  } finally {
    if (abortControllers.get(threadId) === controller) {
      abortControllers.delete(threadId);
    }
  }
}
